import {
  DialogComponent,
  DialogOpenEvent,
  DialogCloseEvent,
} from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

class CartDrawerComponent extends DialogComponent {
  #summaryThreshold = 0.5;

  #historyAbortController = null;
  #restoreAbortController = null;
  #restoreFrame = null;

  #historyScrollY = 0;
  #previousScrollRestoration = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.addEventListener(
      DialogOpenEvent.eventName,
      this.#updateStickyState
    );

    this.addEventListener(
      DialogOpenEvent.eventName,
      this.#handleHistoryOpen
    );

    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleHistoryClose
    );

    if (history.state?.cartDrawerOpen) {
      const state = { ...history.state };
      delete state.cartDrawerOpen;
      history.replaceState(state, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#updateStickyState
    );

    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#handleHistoryOpen
    );

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleHistoryClose
    );

    this.#historyAbortController?.abort();
    this.#historyAbortController = null;

    this.#cancelPendingRestore();
    this.#restoreBrowserScrollMode();
  }

  #cancelPendingRestore = () => {
    this.#restoreAbortController?.abort();
    this.#restoreAbortController = null;

    if (this.#restoreFrame !== null) {
      cancelAnimationFrame(this.#restoreFrame);
      this.#restoreFrame = null;
    }
  };

  #restoreBrowserScrollMode = () => {
    if (this.#previousScrollRestoration !== null) {
      history.scrollRestoration =
        this.#previousScrollRestoration;

      this.#previousScrollRestoration = null;
    }
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    this.#cancelPendingRestore();

    // The shared dialog has already locked the body.
    const bodyTop = parseFloat(document.body.style.top);

    this.#historyScrollY =
      document.body.style.position === 'fixed' &&
      Number.isFinite(bodyTop)
        ? -bodyTop
        : window.scrollY;

    if (this.#previousScrollRestoration === null) {
      this.#previousScrollRestoration =
        history.scrollRestoration;
    }

    history.scrollRestoration = 'manual';

    if (!history.state?.cartDrawerOpen) {
      history.pushState(
        {
          ...history.state,
          cartDrawerOpen: true,
        },
        ''
      );
    }

    this.#historyAbortController?.abort();
    this.#historyAbortController = new AbortController();

    window.addEventListener(
      'popstate',
      this.#handlePopState,
      {
        signal: this.#historyAbortController.signal,
      }
    );
  };

  #restoreHistoryScroll = () => {
    this.#cancelPendingRestore();

    const scrollY = this.#historyScrollY;

    this.#restoreFrame = requestAnimationFrame(() => {
      this.#restoreFrame = null;

      if (
        this.isConnected &&
        !this.refs.dialog?.open &&
        document.body.style.position !== 'fixed'
      ) {
        window.scrollTo({
          top: scrollY,
          behavior: 'instant',
        });
      }

      this.#restoreBrowserScrollMode();
    });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    this.#historyAbortController = null;

    if (this.#previousScrollRestoration === null) return;

    this.#cancelPendingRestore();

    if (history.state?.cartDrawerOpen) {
      this.#restoreAbortController = new AbortController();

      window.addEventListener(
        'popstate',
        this.#restoreHistoryScroll,
        {
          once: true,
          signal: this.#restoreAbortController.signal,
        }
      );

      history.back();
    } else {
      this.#restoreHistoryScroll();
    }
  };

  #handlePopState = async () => {
    const dialog = this.refs.dialog;

    if (!dialog?.open) return;

    dialog.style.setProperty(
      '--dialog-drawer-closing-animation',
      'none'
    );

    try {
      await this.closeDialog();
    } finally {
      dialog.style.removeProperty(
        '--dialog-drawer-closing-animation'
      );
    }
  };

  #handleCartAdd = (event) => {
    if (
      event instanceof CartAddEvent &&
      !event.detail.data?.didError &&
      this.hasAttribute('auto-open')
    ) {
      this.showDialog();
    }

    this.#announceCartCount(
      event.detail.resource?.item_count
    );
  };

  #announceCartCount(cartCount) {
    const liveRegion = this.refs.liveRegion;

    if (
      !this.refs.dialog?.open ||
      !liveRegion ||
      cartCount === undefined
    ) {
      return;
    }

    liveRegion.textContent =
      `${Theme.translations.cart_count}: ${cartCount}`;
  }

  showDialog() {
    if (this.refs.dialog?.open) return;

    for (const menu of document.querySelectorAll('header-drawer')) {
      const details = menu.querySelector(':scope > details');

      if (details?.open && typeof menu.close === 'function') {
        menu.close(undefined, true);
      }
    }

    super.showDialog();
  }

  open() {
    this.showDialog();

    customElements
      .whenDefined('shopify-payment-terms')
      .then(() => {
        if (!this.isConnected) return;

        const installmentsContent = document
          .querySelector('shopify-payment-terms')
          ?.shadowRoot;

        const cta = installmentsContent?.querySelector(
          '#shopify-installments-cta'
        );

        cta?.addEventListener(
          'click',
          this.closeDialog,
          { once: true }
        );
      });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = this.refs;

    if (!dialog) return;

    const content = dialog.querySelector(
      '.cart-drawer__content'
    );

    const summary = dialog.querySelector(
      '.cart-drawer__summary'
    );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );
      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}